import React, { useEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Bitcoin, ExternalLink, Loader2, CheckCircle2, AlertTriangle, Copy as CopyIcon } from 'lucide-react';
import { api } from '../api/client';
import { useT } from '../context/I18nContext';
import { toast } from 'sonner';

const OrderVoucher = () => {
  const { orderNumber } = useParams();
  const { t } = useT();
  const [order, setOrder] = useState(null);
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [voucherCode, setVoucherCode] = useState('');
  const [voucherAmount, setVoucherAmount] = useState('');
  const [providerId, setProviderId] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const openedRef = useRef(false);

  const loadOrder = () => api.getOrder(orderNumber).then(setOrder).catch(() => setOrder(null)).finally(() => setLoading(false));

  useEffect(() => {
    loadOrder();
    api.voucherProviders().then((r) => setProviders(r.providers || [])).catch(() => {});
  }, [orderNumber]);

  // Auto-open the assigned voucher provider ONCE (in a new tab) so customer starts the purchase
  useEffect(() => {
    if (!order || openedRef.current) return;
    if (order.voucher_url) {
      openedRef.current = true;
      window.open(order.voucher_url, '_blank', 'noopener,noreferrer');
      setProviderId(order.voucher_provider_id || '');
    }
    if (order.voucher_code) setSubmitted(true);
  }, [order]);

  const copy = async (text) => {
    try { await navigator.clipboard.writeText(text); toast.success('Copied'); } catch { toast.error('Copy failed'); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (voucherCode.trim().length < 4) return toast.error('Please paste a valid voucher code');
    setSubmitting(true);
    try {
      await api.submitVoucherCode(orderNumber, {
        code: voucherCode.trim(),
        amount: voucherAmount ? parseFloat(voucherAmount) : null,
        provider_id: providerId || order?.voucher_provider_id || '',
      });
      setSubmitted(true);
      toast.success('Voucher code received — we will verify it shortly');
      loadOrder();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not submit voucher code');
    } finally { setSubmitting(false); }
  };

  if (loading) return (<><Header /><main className="bg-black min-h-[60vh] flex items-center justify-center"><Loader2 className="h-6 w-6 text-[#d4af37] animate-spin" /></main><Footer /></>);
  if (!order) return (
    <><Header />
      <main className="bg-black min-h-[60vh] flex items-center justify-center px-6">
        <div className="text-center max-w-md">
          <AlertTriangle className="h-10 w-10 text-rose-400 mx-auto mb-3" />
          <h1 className="serif-italic gold-gradient-text text-4xl">Order not found</h1>
          <Link to="/" className="text-[#d4af37] mt-4 inline-block underline">Back to home</Link>
        </div>
      </main>
      <Footer />
    </>
  );

  const assignedProvider = providers.find((p) => p.id === (order.voucher_provider_id || providerId));

  return (
    <>
      <Header />
      <main className="bg-black min-h-[60vh]">
        <div className="max-w-[900px] mx-auto px-6 py-16">
          <div className="flex items-start justify-between flex-wrap gap-4 mb-8">
            <div>
              <p className="text-[#d4af37] tracking-[0.3em] text-xs uppercase mb-2">Order placed</p>
              <h1 className="serif-italic gold-gradient-text text-5xl" data-testid="voucher-order-number">{order.order_number}</h1>
              <p className="text-zinc-400 mt-2">Total to pay: <span className="text-[#d4af37] text-lg">€{order.total.toFixed(2)}</span></p>
            </div>
            <Badge variant="outline" className={`text-sm px-3 py-1.5 ${submitted ? 'border-emerald-500 text-emerald-400' : 'border-amber-500 text-amber-400'}`}>
              {submitted ? 'Voucher received — verifying' : t('voucher.status')}
            </Badge>
          </div>

          <div className="grid md:grid-cols-2 gap-5">
            <Card className="bg-zinc-950 border-zinc-900 p-6" data-testid="voucher-step-buy">
              <h2 className="serif-italic text-xl text-[#d4af37] mb-3 flex items-center gap-2"><Bitcoin className="h-5 w-5" /> Step 1 · Buy voucher</h2>
              {assignedProvider ? (
                <>
                  <p className="text-zinc-300 text-sm mb-3">A new tab opened with <strong className="text-[#d4af37]">{assignedProvider.name}</strong>. Buy a Bitcoin (or USDT) voucher for exactly <strong className="text-[#d4af37]">€{order.total.toFixed(2)}</strong> using your chosen fiat method.</p>
                  <p className="text-zinc-500 text-xs mb-3">{assignedProvider.description}</p>
                  <a href={assignedProvider.url} target="_blank" rel="noopener noreferrer">
                    <Button variant="outline" className="btn-outline-gold w-full" data-testid={`voucher-open-${assignedProvider.id}`}>
                      Reopen {assignedProvider.name} <ExternalLink className="h-3.5 w-3.5 ml-2" />
                    </Button>
                  </a>
                </>
              ) : (
                <p className="text-zinc-400 text-sm">Please contact support — no voucher provider assigned.</p>
              )}
              <div className="mt-4 border-t border-zinc-900 pt-3 flex items-center gap-2 text-xs text-zinc-500">
                <span>Order total to buy voucher for:</span>
                <button className="font-mono text-[#d4af37] flex items-center gap-1 hover:underline" onClick={() => copy(order.total.toFixed(2))}><CopyIcon className="h-3 w-3" /> €{order.total.toFixed(2)}</button>
              </div>
            </Card>

            <Card className="bg-zinc-950 border-zinc-900 p-6" data-testid="voucher-step-paste">
              <h2 className="serif-italic text-xl text-[#d4af37] mb-3">Step 2 · Paste your voucher code</h2>
              {submitted ? (
                <div className="text-emerald-400 flex items-start gap-2 text-sm bg-emerald-500/10 p-3 rounded" data-testid="voucher-submitted-ok">
                  <CheckCircle2 className="h-5 w-5 shrink-0" />
                  <div>
                    <p>We received your voucher code. We are verifying the balance and will ship your order shortly.</p>
                    <p className="text-emerald-300/70 text-xs mt-1 font-mono">{order.voucher_code || voucherCode}</p>
                  </div>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-3">
                  <div>
                    <Label className="text-zinc-300 text-sm">Voucher / redeem code <span className="text-rose-400">*</span></Label>
                    <Input required value={voucherCode} onChange={(e) => setVoucherCode(e.target.value)} className="bg-transparent border-zinc-800 mt-1 font-mono" placeholder="e.g. BR-XXXXXXXX-XXXX" data-testid="voucher-code-input" />
                  </div>
                  <div>
                    <Label className="text-zinc-300 text-sm">Voucher amount (€) <span className="text-zinc-500 text-xs">optional</span></Label>
                    <Input type="number" step="0.01" value={voucherAmount} onChange={(e) => setVoucherAmount(e.target.value)} className="bg-transparent border-zinc-800 mt-1" placeholder={order.total.toFixed(2)} data-testid="voucher-amount-input" />
                  </div>
                  <Button type="submit" disabled={submitting} className="btn-gold w-full h-11" data-testid="voucher-submit">
                    {submitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />…</> : 'Submit voucher code'}
                  </Button>
                  <p className="text-zinc-500 text-xs">We&rsquo;ll verify the balance manually and ship once confirmed — usually within a few hours.</p>
                </form>
              )}
            </Card>
          </div>

          <Card className="bg-zinc-950 border-zinc-900 p-5 mt-5">
            <h3 className="serif-italic text-lg text-[#d4af37] mb-3">Order summary</h3>
            <ul className="space-y-1.5 text-sm text-zinc-300">
              {order.items.map((i, idx) => (
                <li key={`${i.product_id || 'item'}-${idx}`} className="flex justify-between"><span>{i.name} × {i.qty}</span><span>€{(i.price * i.qty).toFixed(2)}</span></li>
              ))}
            </ul>
            <div className="border-t border-zinc-800 mt-3 pt-3 space-y-1 text-sm">
              <div className="flex justify-between text-zinc-400"><span>Subtotal</span><span>€{order.subtotal.toFixed(2)}</span></div>
              {order.shipping_cost > 0 && <div className="flex justify-between text-zinc-400"><span>Shipping</span><span>€{order.shipping_cost.toFixed(2)}</span></div>}
              <div className="flex justify-between text-zinc-100 pt-2 border-t border-zinc-800 mt-2">
                <span className="text-base">Total</span>
                <span className="text-[#d4af37] text-xl" data-testid="voucher-total">€{order.total.toFixed(2)}</span>
              </div>
            </div>
          </Card>
        </div>
      </main>
      <Footer />
    </>
  );
};

export default OrderVoucher;
